import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronRight, Rocket } from "lucide-react";
import { getAuditTrail } from "../../services/inHouseService";
import CopyButton from "../ui/CopyButton";
import { BasicsStep, authLabel } from "./ConnectionSteps";
import { DangerZone, DecommissionCard, LifecycleCard, SurfacesCard, groupCalls, historyLabel } from "./EndpointSteps";
import { Card, StatusBadge, statusOf } from "./parts";

/**
 * Overview and Settings for an integration that is already published. The setup steps are
 * for getting a draft live; once it is, admins come here to check it and change one thing.
 */

const RECENT_CHANGES = 5;

/** One row of the details card: what it is, its value, and where to change it. */
function DetailRow({ label, children, action }) {
  return (
    <div className="grid gap-2 px-6 py-4 sm:grid-cols-[140px_minmax(0,1fr)_auto] sm:items-center sm:gap-6">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="min-w-0 text-sm text-gray-900">{children}</dd>
      {action && <div className="flex shrink-0 items-center gap-3">{action}</div>}
    </div>
  );
}

function LinkButton({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-0.5 text-sm font-medium text-blue-600 hover:text-blue-700">
      {children}
      <ChevronRight className="h-4 w-4" />
    </button>
  );
}

const formatUpdated = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/** The last few History entries, so the overview answers "what changed lately?" at a glance. */
function RecentChanges({ integration, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let current = true;
    getAuditTrail(integration.id, { limit: RECENT_CHANGES, offset: 0 }).then((r) => current && setRows(r.success ? r.data : []));
    return () => {
      current = false;
    };
  }, [integration.id, integration.updated_at]);

  return (
    <Card title="Recent changes" action={<LinkButton onClick={() => onOpen("history")}>All history</LinkButton>}>
      {rows === null && <p className="text-sm text-gray-500">Loading…</p>}
      {rows?.length === 0 && <p className="text-sm text-gray-500">No changes recorded yet.</p>}
      {rows?.length > 0 && (
        <ol className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="min-w-0 truncate text-gray-900">
                {historyLabel(r.action)}
                {r.summary && <span className="text-gray-500"> · {r.summary}</span>}
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {r.actor_email || "Someone"} · {formatUpdated(r.created_at)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export function OverviewPanel({ integration, lifecycle, onOpen }) {
  const status = statusOf(integration);
  const endpoints = integration.endpoints;
  const published = endpoints.filter((e) => e.lifecycle_state === "published").length;
  const connected = !!integration.connection?.connected;
  const baseUrl = integration.api?.base_url;
  const updated = formatUpdated(integration.updated_at);
  const groups = groupCalls(endpoints);

  return (
    <div className="space-y-6">
      {status === "Disabled" && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="flex-1 text-amber-900">
            <span className="font-semibold">Calls are paused.</span> Routes and workflows that use them fail until you turn it back on.
          </p>
          <button
            type="button"
            disabled={lifecycle.busy}
            onClick={() => lifecycle.run("turnOn")}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Rocket className="h-4 w-4" /> Turn back on
          </button>
        </div>
      )}

      {/* One details card, as Stripe shows an object: each row says what it is and where to change it. */}
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <dl className="divide-y divide-gray-100">
          <DetailRow label="Status">
            <span className="flex flex-wrap items-center gap-3">
              <StatusBadge status={status} />
              {updated && <span className="text-xs text-gray-500">Updated {updated}</span>}
            </span>
          </DetailRow>
          <DetailRow label="Address" action={<LinkButton onClick={() => onOpen("connection")}>Edit</LinkButton>}>
            {baseUrl ? (
              <span className="flex min-w-0 items-center gap-2">
                <code title={baseUrl} className="min-w-0 truncate font-mono text-sm text-gray-800">{baseUrl}</code>
                <CopyButton value={baseUrl} />
              </span>
            ) : (
              <span className="text-gray-500">Not set</span>
            )}
          </DetailRow>
          <DetailRow label="Sign-in" action={<LinkButton onClick={() => onOpen("connection")}>Edit</LinkButton>}>
            <span className="flex items-center gap-1.5">
              {connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
              {authLabel(integration.api?.auth?.kind)} · {connected ? "details saved" : "details not saved"}
            </span>
          </DetailRow>
          <DetailRow
            label="Calls"
            action={
              <>
                <LinkButton onClick={() => onOpen("test")}>Try a call</LinkButton>
                <LinkButton onClick={() => onOpen("calls")}>Manage</LinkButton>
              </>
            }
          >
            <p>
              {published} published
              {endpoints.length > published && <span className="text-gray-500"> · {endpoints.length - published} draft</span>}
            </p>
            {groups.length > 1 && (
              <p className="mt-1.5 flex flex-wrap gap-1.5">
                {groups.map(([name, calls]) => (
                  <span key={name} className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                    {name ? `/${name}` : "Other"} {calls.length}
                  </span>
                ))}
              </p>
            )}
          </DetailRow>
        </dl>
      </section>

      <RecentChanges integration={integration} onOpen={onOpen} />
      <SurfacesCard />
    </div>
  );
}

export function SettingsPanel({ integration, logo, lifecycle, onSaveBasics, onDecommissioned }) {
  return (
    <div className="space-y-6">
      <BasicsStep integration={integration} logo={logo} onSave={onSaveBasics} onNext={() => {}} submitLabel="Save changes" />
      <DangerZone>
        <LifecycleCard integration={integration} lifecycle={lifecycle} />
        <DecommissionCard integration={integration} onDone={onDecommissioned} />
      </DangerZone>
    </div>
  );
}
